import React, { useEffect, useState, useRef } from 'react';
import { useApp, api, API } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send } from 'lucide-react';

export default function MessagesPage() {
  const { t, lang, user } = useApp();
  const [peer, setPeer] = useState('');
  const [activePeer, setActivePeer] = useState('');
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  // suggested peers
  const defaultPeer = user?.role === 'parent' ? 'teacher@zayed.ae' : (user?.role === 'teacher' ? 'parent@zayed.ae' : '');

  useEffect(() => { if (defaultPeer && !peer) setPeer(defaultPeer); }, [defaultPeer, peer]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const open = async () => {
    if (!peer) return;
    setActivePeer(peer);
    const { data } = await api.get('/messages', { params: { peer } });
    setMessages(data);
    // open WS
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} }
    const token = localStorage.getItem('zayed_token');
    const wsUrl = API.replace(/^http/, 'ws') + `/ws/messages?token=${encodeURIComponent(token)}&peer=${encodeURIComponent(peer)}`;
    const ws = new WebSocket(wsUrl);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      } catch {}
    };
    wsRef.current = ws;
  };

  useEffect(() => { return () => { if (wsRef.current) try { wsRef.current.close(); } catch(e){} }; }, []);

  const send = (e) => {
    e?.preventDefault?.();
    const txt = body.trim();
    if (!txt || !activePeer) return;
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ body: txt }));
    } else {
      // fallback REST
      api.post('/messages', { to_email: activePeer, body: txt }).then(r => setMessages([...messages, r.data]));
    }
    setBody('');
  };

  return (
    <main id="main" className="max-w-4xl mx-auto px-4 py-8" data-testid="messages-page">
      <h1 className="text-3xl font-black mb-6">{t('messages_title')}</h1>
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Input placeholder={t('messages_to')} value={peer} onChange={(e) => setPeer(e.target.value)} className="max-w-md" data-testid="msg-peer-input" />
          <Button onClick={open} disabled={!peer} className="bg-primary" data-testid="msg-open-chat-btn">{t('messages_open_chat')}</Button>
        </CardHeader>
        <CardContent>
          {!activePeer ? (
            <p className="text-center text-muted-foreground py-12">{t('messages_empty')}</p>
          ) : (
            <>
              <div className="border border-border rounded-lg p-3 h-[420px] overflow-y-auto bg-secondary/30 mb-3" data-testid="msg-thread">
                {messages.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t('messages_empty')}</p>
                ) : (
                  messages.map(m => {
                    const mine = m.from_email === user?.email;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`msg-${m.id}`}>
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${mine ? 'bg-primary text-primary-foreground' : 'bg-white border border-border'}`}>
                          <div className="text-[10px] opacity-70 mb-1">{mine ? user?.full_name : m.from_name}</div>
                          <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={send} className="flex gap-2">
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('messages_placeholder')} data-testid="msg-input" />
                <Button type="submit" className="bg-primary" data-testid="msg-send-btn"><Send className="w-4 h-4 me-1" />{t('messages_send')}</Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
